/**
 * AirportDiagramController — fetches and renders ForeFlight-style
 * airport diagrams (OSM aeroway taxiways/runways/aprons/helipads)
 * as a vector overlay at high zoom levels.
 *
 * Boss request 2026-05-18 22:42 PT. Design at
 * `data/taxiway-diagrams-plan.md`.
 *
 * Activation rules:
 *   - User toggle `layers.airportDiagrams` must be ON (default true)
 *   - Map zoom must be >= ACTIVATE_ZOOM (default 13.5) so we don't
 *     spam diagrams while the user is at en-route zoom levels
 *
 * Data flow:
 *   - On zoom or pan, find the airports currently rendered inside the
 *     map's viewport (queryRenderedFeatures on the 'airports' layer).
 *   - For each ident not already in our in-memory cache, fetch
 *     `/api/airport-diagram?ident=XXX` and merge the FeatureCollection
 *     into a combined `airport-diagrams` GeoJSON source.
 *   - Memory cache is unbounded for the session; the server-side Blob
 *     + edge cache mean subsequent fetches are cheap.
 *
 * Rendering:
 *   - Fill layers: aprons (medium grey), runways (dark grey w/ light
 *     border at z>=15)
 *   - Line layers: taxiways (yellow w/ black halo)
 *   - Symbol layer: helipads (cyan H)
 *
 * Effect-only component — returns null.
 */
import { useEffect, useRef } from 'react';
import type { GeoJSONSource } from 'maplibre-gl';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { getMapInstance, safeGetSource, EMPTY_FC } from '../MapView';
import { registerStyleRebuild } from '../layerVisibilityRegistry';

const SOURCE_ID = 'airport-diagrams';
const ACTIVATE_ZOOM = 13.5;
// Refresh visible-airport set at most every 500ms while the user is
// panning/zooming; the heavy work is the network fetch so additional
// debouncing isn't critical but it cuts queryRenderedFeatures calls.
const VIEWPORT_DEBOUNCE_MS = 250;

interface DiagramFetchResult {
  ok: boolean;
  ident: string;
  features?: GeoJSON.FeatureCollection;
  error?: string;
}

// Session-lifetime cache of fetched diagrams (ident -> FeatureCollection).
// Module-scoped so navigating away from /map and back doesn't re-fetch.
const diagramCache = new Map<string, GeoJSON.FeatureCollection>();
// In-flight fetch dedupe so two viewport events for the same ident
// don't trigger two HTTP requests.
const inFlight = new Map<string, Promise<DiagramFetchResult>>();

async function fetchDiagram(ident: string, signal?: AbortSignal): Promise<DiagramFetchResult> {
  const url = `/api/airport-diagram?ident=${encodeURIComponent(ident)}`;
  try {
    const r = await fetch(url, { signal });
    if (!r.ok) {
      return { ok: false, ident, error: `HTTP ${r.status}` };
    }
    const data = (await r.json()) as {
      ok?: boolean;
      ident?: string;
      features?: GeoJSON.FeatureCollection;
      error?: string;
    };
    if (!data.ok || !data.features) {
      return { ok: false, ident, error: data.error ?? 'no features' };
    }
    return { ok: true, ident, features: data.features };
  } catch (e) {
    return { ok: false, ident, error: e instanceof Error ? e.message : String(e) };
  }
}

function getOrFetchDiagram(ident: string): Promise<DiagramFetchResult> {
  if (diagramCache.has(ident)) {
    return Promise.resolve({ ok: true, ident, features: diagramCache.get(ident) });
  }
  const existing = inFlight.get(ident);
  if (existing) return existing;
  const p = fetchDiagram(ident).then((r) => {
    if (r.ok && r.features) diagramCache.set(ident, r.features);
    inFlight.delete(ident);
    return r;
  });
  inFlight.set(ident, p);
  return p;
}

/**
 * Merge all currently-cached diagrams into a single GeoJSON
 * FeatureCollection, tagged with each feature's airport ident so the
 * client could (later) filter or highlight specific airports.
 */
function buildMergedFC(idents: ReadonlyArray<string>): GeoJSON.FeatureCollection {
  const out: GeoJSON.Feature[] = [];
  for (const ident of idents) {
    const fc = diagramCache.get(ident);
    if (!fc) continue;
    for (const f of fc.features) {
      // Stamp ident so downstream paint expressions / pop-ups can
      // disambiguate when two airports overlap (rare but happens for
      // adjacent fields like 1O5/1O6).
      const props = { ...(f.properties ?? {}), airport: ident };
      out.push({ ...f, properties: props });
    }
  }
  return { type: 'FeatureCollection', features: out };
}

// HI-1 step 3 (2026-05-23): rebuildRef prop removed. The controller now
// registers its style-rebuild restore with layerVisibilityRegistry.
export function AirportDiagramController() {
  const enabled = useLayerVisibilityStore((s) => s.layers.airportDiagrams);

  // Track the set of idents we've fetched so we can rebuild the merged
  // FC on each viewport scan without re-iterating the whole cache.
  const visibleIdentsRef = useRef<Set<string>>(new Set());

  // Push the merged FC into the source. Safe to call any time \u2014 if the
  // source doesn't exist yet (style not loaded), it's a no-op.
  const pushMerged = (): void => {
    const map = getMapInstance();
    if (!map) return;
    const src = safeGetSource<GeoJSONSource>(map, SOURCE_ID);
    if (!src) return;
    if (visibleIdentsRef.current.size === 0) {
      src.setData(EMPTY_FC);
      return;
    }
    src.setData(buildMergedFC(Array.from(visibleIdentsRef.current)));
  };

  // Scan the airports source under the viewport, kick off fetches for
  // any new idents, and rebuild the merged FC.
  const scanViewport = (): void => {
    if (!enabled) return;
    const map = getMapInstance();
    if (!map) return;
    const z = map.getZoom();
    if (z < ACTIVATE_ZOOM) {
      // Below activation zoom \u2014 clear everything.
      if (visibleIdentsRef.current.size > 0) {
        visibleIdentsRef.current = new Set();
        pushMerged();
      }
      return;
    }

    // queryRenderedFeatures targets the layer ids the airports source
    // exposes. The MapView uses one or two layers; we union them.
    let feats: ReturnType<typeof map.queryRenderedFeatures> = [];
    try {
      feats = map.queryRenderedFeatures(undefined, {
        // Layer ids used by MapView's airport rendering. We deliberately
        // list every candidate so a future relayer doesn't silently
        // disable this controller.
        // MapView's airport source renders via these two layers.
        // Note: 'airports-icon' (singular) not 'airports-icons'.
        layers: ['airports-icon', 'airports-label'].filter(
          (id) => Boolean(map.getLayer(id)),
        ),
      });
    } catch {
      // Race: layer not yet registered. Try again on next event.
      return;
    }

    const newSet = new Set<string>();
    for (const f of feats) {
      const ident = (f.properties as { ident?: string })?.ident;
      if (typeof ident === 'string' && ident.length >= 2 && ident.length <= 5) {
        newSet.add(ident.toUpperCase());
      }
    }
    // Cap how many idents we query at once to keep network usage sane
    // \u2014 at very high zoom this is usually 1-2 airports; cap at 8 just
    // in case the user is over a metroplex (KSFO/KOAK/KSJC etc.).
    const idents = Array.from(newSet).slice(0, 8);
    visibleIdentsRef.current = new Set(idents);

    // Fire fetches for any not-yet-cached idents. As each resolves, we
    // re-push the merged FC so the user sees diagrams appear
    // progressively rather than waiting for the slowest one.
    for (const id of idents) {
      if (diagramCache.has(id)) continue;
      void getOrFetchDiagram(id).then((r) => {
        if (r.ok && visibleIdentsRef.current.has(r.ident)) {
          pushMerged();
        }
      });
    }
    // Push whatever we have now (already-cached idents) so we don't
    // wait on a network round-trip if everything was warm.
    pushMerged();
  };

  // Effect: install source + layers on mount, manage map listeners.
  useEffect(() => {
    // install() calls getMapInstance() internally so it's safe to call
    // before the map is ready, and rebuildRef can be set immediately.
    // (Child useEffects fire before the parent MapView effect that sets
    // mapInstance, so getMapInstance() is always null on first mount.
    // MapView calls rebuildRef.current() from map.on('load') which is
    // where the real initialisation happens.)
    let listenersInstalled = false;
    let debounceTimer: number | null = null;

    const onMoveOrZoom = (): void => {
      if (debounceTimer != null) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        scanViewport();
      }, VIEWPORT_DEBOUNCE_MS);
    };

    const install = (): void => {
      const map = getMapInstance();
      if (!map) return;
      // Register map event listeners once the map is available.
      if (!listenersInstalled) {
        map.on('moveend', onMoveOrZoom);
        map.on('zoomend', onMoveOrZoom);
        map.on('idle', onMoveOrZoom);
        listenersInstalled = true;
      }
      if (!map.getSource(SOURCE_ID)) {
        try {
          map.addSource(SOURCE_ID, { type: 'geojson', data: EMPTY_FC });
        } catch { /* race \u2014 source already added */ }
      }
      // Aprons (polygon fill) \u2014 sits under everything.
      if (!map.getLayer('apt-apron-fill')) {
        try {
          map.addLayer({
            id: 'apt-apron-fill',
            type: 'fill',
            source: SOURCE_ID,
            filter: ['==', ['get', 'aeroway'], 'apron'],
            paint: {
              'fill-color': '#3d4654',
              'fill-opacity': 0.7,
            },
            minzoom: ACTIVATE_ZOOM,
          });
        } catch { /* race */ }
      }
      // Runway polygons (asphalt-grey fill). When mappers used a
      // LineString for the runway, the line layer below picks it up.
      if (!map.getLayer('apt-runway-fill')) {
        try {
          map.addLayer({
            id: 'apt-runway-fill',
            type: 'fill',
            source: SOURCE_ID,
            filter: ['all', ['==', ['get', 'aeroway'], 'runway'], ['==', ['geometry-type'], 'Polygon']],
            paint: {
              'fill-color': '#2a2a2a',
              'fill-outline-color': '#cccccc',
            },
            minzoom: ACTIVATE_ZOOM,
          });
        } catch { /* race */ }
      }
      // Runway centerlines (when stored as LineString).
      if (!map.getLayer('apt-runway-line')) {
        try {
          map.addLayer({
            id: 'apt-runway-line',
            type: 'line',
            source: SOURCE_ID,
            filter: ['all', ['==', ['get', 'aeroway'], 'runway'], ['==', ['geometry-type'], 'LineString']],
            paint: {
              'line-color': '#2a2a2a',
              'line-width': [
                'interpolate', ['linear'], ['zoom'],
                12, 4,
                15, 12,
                18, 30,
              ],
              'line-opacity': 0.9,
            },
            layout: { 'line-cap': 'butt', 'line-join': 'miter' },
            minzoom: ACTIVATE_ZOOM,
          });
        } catch { /* race */ }
      }
      // Taxiway lines.
      if (!map.getLayer('apt-taxiway-halo')) {
        try {
          map.addLayer({
            id: 'apt-taxiway-halo',
            type: 'line',
            source: SOURCE_ID,
            filter: ['==', ['get', 'aeroway'], 'taxiway'],
            paint: {
              'line-color': '#000000',
              'line-opacity': 0.55,
              'line-width': [
                'interpolate', ['linear'], ['zoom'],
                13, 2,
                15, 6,
                18, 14,
              ],
            },
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            minzoom: ACTIVATE_ZOOM,
          });
        } catch { /* race */ }
      }
      if (!map.getLayer('apt-taxiway-line')) {
        try {
          map.addLayer({
            id: 'apt-taxiway-line',
            type: 'line',
            source: SOURCE_ID,
            filter: ['==', ['get', 'aeroway'], 'taxiway'],
            paint: {
              'line-color': '#e6b800',
              'line-width': [
                'interpolate', ['linear'], ['zoom'],
                13, 1,
                15, 4,
                18, 10,
              ],
            },
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            minzoom: ACTIVATE_ZOOM,
          });
        } catch { /* race */ }
      }
      // Taxiway labels (ref \u2014 e.g. "A", "B1").
      if (!map.getLayer('apt-taxiway-label')) {
        try {
          map.addLayer({
            id: 'apt-taxiway-label',
            type: 'symbol',
            source: SOURCE_ID,
            filter: ['all',
              ['==', ['get', 'aeroway'], 'taxiway'],
              ['has', 'ref'],
            ],
            layout: {
              'text-field': ['get', 'ref'],
              'text-size': 11,
              'text-font': ['Noto Sans Regular'],
              'symbol-placement': 'line',
              'text-allow-overlap': false,
            },
            paint: {
              'text-color': '#1a1a1a',
              'text-halo-color': '#e6b800',
              'text-halo-width': 1.5,
            },
            minzoom: 14.0,
          });
        } catch { /* race */ }
      }
      // Helipads \u2014 cyan ring + H symbol when present (small).
      if (!map.getLayer('apt-helipad-fill')) {
        try {
          map.addLayer({
            id: 'apt-helipad-fill',
            type: 'fill',
            source: SOURCE_ID,
            filter: ['all', ['==', ['get', 'aeroway'], 'helipad'], ['==', ['geometry-type'], 'Polygon']],
            paint: {
              'fill-color': '#1e6f9b',
              'fill-outline-color': '#7fd6ff',
              'fill-opacity': 0.85,
            },
            minzoom: ACTIVATE_ZOOM,
          });
        } catch { /* race */ }
      }
      // Hangars \u2014 light blue tint, very subtle. Helps the diagram
      // not look empty around small fields where the only building is
      // the hangar.
      if (!map.getLayer('apt-hangar-fill')) {
        try {
          map.addLayer({
            id: 'apt-hangar-fill',
            type: 'fill',
            source: SOURCE_ID,
            filter: ['==', ['get', 'aeroway'], 'hangar'],
            paint: {
              'fill-color': '#4a5567',
              'fill-opacity': 0.45,
              'fill-outline-color': '#8aa0bb',
            },
            minzoom: 14.0,
          });
        } catch { /* race */ }
      }
    };

    // HI-1 step 2/3: style-rebuild restore registered with the central
    // registry. MapView's styledata + map.on('load') handlers call us
    // via runStyleRebuildHandlers — no prop-drilled ref.
    const restore = (): void => {
      install();
      scanViewport();
    };
    const unregister = registerStyleRebuild({
      id: 'airportDiagram',
      restore,
    });

    install();
    scanViewport();

    return () => {
      if (debounceTimer != null) window.clearTimeout(debounceTimer);
      const map = getMapInstance();
      if (map && listenersInstalled) {
        try { map.off('moveend', onMoveOrZoom); } catch { /* noop */ }
        try { map.off('zoomend', onMoveOrZoom); } catch { /* noop */ }
        try { map.off('idle', onMoveOrZoom); } catch { /* noop */ }
      }
      unregister();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toggle: when user turns the layer off, clear the visible idents
  // and push EMPTY_FC. When they turn it back on, scan immediately.
  useEffect(() => {
    if (!enabled) {
      visibleIdentsRef.current = new Set();
      const map = getMapInstance();
      if (map) {
        const src = safeGetSource<GeoJSONSource>(map, SOURCE_ID);
        src?.setData(EMPTY_FC);
      }
      return;
    }
    scanViewport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return null;
}
